/**
 * Haptic feedback types that can be triggered on iOS devices
 */
export enum HapticType {
  Success = "success",
  Error = "error",
  Warning = "warning",
  Selection = "selection",
  ImpactLight = "impactLight",
  ImpactMedium = "impactMedium",
  ImpactHeavy = "impactHeavy",
  ImpactSoft = "impactSoft",
  ImpactRigid = "impactRigid",
}

/**
 * Checks if the current platform supports haptic feedback
 * @returns boolean indicating if haptic feedback is supported
 */
export const isHapticSupported = (): boolean => {
  return !!window.webkit?.messageHandlers["haptic-request"];
};

/**
 * Triggers haptic feedback on supported platforms
 * @param type The type of haptic feedback to trigger
 * @returns boolean indicating if the haptic feedback was triggered
 */
export const triggerHaptic = (type: HapticType): boolean => {
  try {
    switch (type) {
      case HapticType.Selection:
        androidSelection();
        break;
      case HapticType.ImpactSoft:
        androidSoft();
        break;
      case HapticType.ImpactMedium:
        androidMedium();
        break;
      case HapticType.Success:
        androidSuccess();
        break;
      case HapticType.Error:
        androidError();
        break;
      default:
        androidSelection();
        break;
    }
  } catch (error) {
    console.error("Failed to trigger haptic feedback:", error);
    return false;
  }

  if (!isHapticSupported()) {
    console.log("Haptic feedback not supported on this platform");
    return false;
  }
  try {
    // Send the haptic request to the native app
    window.webkit?.messageHandlers["haptic-request"].postMessage({
      type: type,
    });
    return true;
  } catch (error) {
    console.error("Failed to trigger haptic feedback:", error);
    return false;
  }
};

function androidSelection() {
  if (navigator.vibrate) {
    navigator.vibrate(4);
  }
}

function androidSoft() {
  if (navigator.vibrate) {
    navigator.vibrate(7);
  }
}

function androidMedium() {
  if (navigator.vibrate) {
    navigator.vibrate(10);
  }
}

function androidSuccess() {
  if (navigator.vibrate) {
    navigator.vibrate(20);
  }
}

function androidError() {
  androidVibratePattern(30, 100, 30);
}

function androidVibratePattern(...durations: number[]) {
  if (navigator.vibrate) {
    // For sequential vibrations with pauses between them
    const pattern: number[] = [];
    durations.forEach((duration, index) => {
      pattern.push(duration); // Vibration duration
      if (index < durations.length - 1) {
        pattern.push(20); // Pause between vibrations (50ms)
      }
    });
    navigator.vibrate(pattern);
  }
}

/**
 * Convenience functions for common haptic feedback patterns
 */
export const haptic = {
  /**
   * Trigger a success haptic feedback
   * This one is cheerful and bouncy.
   */
  success: () => triggerHaptic(HapticType.Success),

  /**
   * Trigger an error haptic feedback
   * Error was complex and quick.
   */
  error: () => triggerHaptic(HapticType.Error),

  /**
   * Trigger a warning haptic feedback
   * Julia doesnt like this one. Use with caution.
   */
  warning: () => triggerHaptic(HapticType.Warning),

  /**
   * Trigger a selection haptic feedback
   * Good for subtitles, wordclicks, very faint and good for the start of the drag
   * it is light and pleasant but not too bothersome, reminds you that you did something important
   */
  selection: () => triggerHaptic(HapticType.Selection),

  /**
   * Trigger a light impact haptic feedback
   * Not nearly as friendly and bouncy as success, its somewhere between selection and success
   */
  light: () => triggerHaptic(HapticType.ImpactLight),

  /**
   * Trigger a medium impact haptic feedback
   * Same as light but just stronger. A bit LONGER of a press than light.
   */
  medium: () => triggerHaptic(HapticType.ImpactMedium),

  /**
   * Trigger a heavy impact haptic feedback
   * feels the same as medium.
   */
  heavy: () => triggerHaptic(HapticType.ImpactHeavy),

  /**
   * Trigger a soft impact haptic feedback
   * Lovely almost leaves you wanting more.
   * Soft is too flirty for our app so avoid it.
   * "It's really nice thought" - Julia
   */
  soft: () => triggerHaptic(HapticType.ImpactSoft),

  /**
   * Trigger a rigid impact haptic feedback
   * Stronger and sharper than heavy.
   * It feels like a jab instead of a wet towel slap.
   */
  rigid: () => triggerHaptic(HapticType.ImpactRigid),
};
